import React, { useState, useEffect, useCallback } from 'react';
import { 
  FolderOpen,
  User,
  Mail,
  Clock,
  CheckCircle,
  AlertCircle,
  Pause,
  XCircle,
  Package,
  Calendar,
  DollarSign,
  Play,
  RefreshCw,
  Plus
} from 'lucide-react';
import { projectManagementApiService, ProjectManagement } from '../services/projectManagementApi';

interface ServiceProjectsTabProps {
  isAdmin?: boolean;
  onCreateProject?: () => void;
}

const ServiceProjectsTab: React.FC<ServiceProjectsTabProps> = ({ isAdmin = false, onCreateProject }) => {
  const [projects, setProjects] = useState<ProjectManagement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      let projectsData: ProjectManagement[];
      if (isAdmin) {
        // Les admins voient tous les projets avec les détails complets
        projectsData = await projectManagementApiService.getProjectsManagement();
      } else {
        // Les utilisateurs voient leurs projets avec les étapes de conception
        projectsData = await projectManagementApiService.getUserProjectsWithSteps();
      }
      
      setProjects(projectsData);
    } catch (err) {
      console.error('Erreur lors du chargement des projets:', err);
      setError('Erreur lors du chargement des projets');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  const refreshProjects = async () => {
    try {
      setRefreshing(true);
      await loadProjects();
    } catch (err) {
      console.error('Erreur lors du rafraîchissement:', err);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'created': return <AlertCircle className="w-4 h-4 text-blue-500" />;
      case 'in_progress': return <Play className="w-4 h-4 text-orange-500" />;
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'on_hold': return <Pause className="w-4 h-4 text-yellow-500" />;
      case 'cancelled': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStepStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4 text-gray-500" />;
      case 'in_progress': return <Play className="w-4 h-4 text-orange-500" />;
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-500" />;
      default: return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'created': return 'Créé';
      case 'in_progress': return 'En cours';
      case 'completed': return 'Terminé';
      case 'on_hold': return 'En pause';
      case 'cancelled': return 'Annulé';
      default: return status;
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR').format(price) + ' F CFA';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des projets...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Erreur</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={refreshProjects}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* En-tête avec boutons */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            {isAdmin ? 'Projets Services/Packs' : 'Mes Projets Services/Packs'}
          </h2>
          <p className="text-gray-600">
            {isAdmin 
              ? 'Projets créés depuis les factures confirmées avec gestion des étapes'
              : 'Projets créés par notre équipe suite à vos commandes'
            }
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={refreshProjects}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
          {isAdmin && onCreateProject && (
            <button
              onClick={onCreateProject}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Créer un Projet
            </button>
          )}
        </div>
      </div>

      {/* Statistiques */}
      {projects.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <FolderOpen className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Projets</p>
                <p className="text-2xl font-bold text-gray-900">{projects.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Play className="w-8 h-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">En cours</p>
                <p className="text-2xl font-bold text-gray-900">
                  {projects.filter(p => p.status === 'in_progress').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Terminés</p>
                <p className="text-2xl font-bold text-gray-900">
                  {projects.filter(p => p.status === 'completed').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <DollarSign className="w-8 h-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Valeur Totale</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatPrice(projects.reduce((sum, p) => sum + p.total_amount, 0))}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Liste des projets */}
      <div className="space-y-6">
        {projects.map((project) => (
          <div key={project.project_id} className="bg-white rounded-lg shadow">
            {/* En-tête du projet */}
            <div className="p-6 border-b border-gray-200">
              {/* Message de félicitations pour projets terminés */}
              {project.status === 'completed' && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <div>
                      <h4 className="text-lg font-semibold text-green-800">🎉 Félicitations !</h4>
                      <p className="text-green-700">
                        {isAdmin 
                          ? `Le projet #${project.project_id} a été terminé avec succès !`
                          : 'Votre projet a été terminé avec succès ! Merci de votre confiance.'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(project.status)}
                    <h3 className="text-xl font-semibold text-gray-900">
                      Projet #{project.project_id}
                    </h3>
                    <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                      {getStatusLabel(project.status)}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Montant total</p>
                  <p className="text-xl font-bold text-gray-900">{formatPrice(project.total_amount)}</p>
                </div>
              </div>

              {/* Informations client (pour admin) */}
              {isAdmin && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">Client:</span>
                    <span className="font-medium">{project.client_name}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium">{project.client_email}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">Créé le:</span>
                    <span className="font-medium">{formatDate(project.created_at)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Services du projet */}
            <div className="p-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Services et Étapes de Conception</h4>
              
              <div className="space-y-4">
                {project.services.map((service) => (
                  <div key={service.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Package className="w-5 h-5 text-blue-600" />
                        <div>
                          <h5 className="font-medium text-gray-900">{service.service_name}</h5>
                          {service.pack_name && (
                            <p className="text-sm text-purple-600">Pack: {service.pack_name}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">
                          Quantité: {service.quantity} × {formatPrice(service.unit_price)}
                        </p>
                        <p className="font-medium text-gray-900">{formatPrice(service.total_price)}</p>
                      </div>
                    </div>

                    {/* Étapes de conception */}
                    {service.conception_steps && service.conception_steps.length > 0 && (
                      <div className="mt-4">
                        <h6 className="text-sm font-medium text-gray-700 mb-2">Étapes de conception :</h6>
                        <div className="space-y-2">
                          {service.conception_steps
                            .sort((a, b) => a.order_index - b.order_index)
                            .map((step, index) => (
                              <div key={step.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                                <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium">
                                  {index + 1}
                                </span>
                                {getStepStatusIcon(step.status)}
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900">{step.name}</p>
                                  {step.description && (
                                    <p className="text-xs text-gray-600">{step.description}</p>
                                  )}
                                </div>
                                {step.estimated_duration && (
                                  <span className="text-xs text-gray-500">
                                    {step.estimated_duration}h
                                  </span>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Message si pas d'étapes */}
                    {(!service.conception_steps || service.conception_steps.length === 0) && (
                      <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                        <p className="text-sm text-yellow-800">
                          {isAdmin 
                            ? 'Aucune étape de conception définie. Cliquez pour ajouter des étapes.'
                            : 'Les étapes seront ajoutées par l\'équipe de développement.'
                          }
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}

        {projects.length === 0 && (
          <div className="text-center py-12">
            <FolderOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {isAdmin ? 'Aucun projet trouvé' : 'Aucun projet pour le moment'}
            </h3>
            <p className="text-gray-600">
              {isAdmin 
                ? 'Les projets créés apparaîtront ici.'
                : 'Vos projets apparaîtront ici une fois qu\'ils seront créés par notre équipe.'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceProjectsTab;
